import type { NextRouter } from 'next/router'

interface NotificationNavigationTarget {
  navigation_url?: string | null
  event_type?: string
}

interface NotificationNavigationHandlers {
  router: NextRouter
  onOpenBillingDetail?: (billingUuid: string) => void
  onOpenNfseDetail?: (nfseUuid: string) => void
  onOpenReportModal?: () => void
  onOpenBalanceModal?: (tab: string, disputeUuid?: string) => void
  onCloseDrawer?: () => void
}

// Indica se a notificação tem algum destino navegável (modal, rota ou relatório).
// Usado para só tornar o toast/item clicável quando há para onde ir.
export const notificationHasNavigationTarget = ({
  navigation_url,
  event_type
}: NotificationNavigationTarget): boolean =>
  event_type === 'report_completed' ||
  event_type === 'report_failed' ||
  !!navigation_url

// Navegação única para notificações in-app — reutilizada pelo item da central e
// pelo toast em tempo real, para que ambos abram exatamente o mesmo destino.
export const navigateFromNotification = (
  { navigation_url, event_type }: NotificationNavigationTarget,
  {
    router,
    onOpenBillingDetail,
    onOpenNfseDetail,
    onOpenReportModal,
    onOpenBalanceModal,
    onCloseDrawer
  }: NotificationNavigationHandlers
): void => {
  if (
    (event_type === 'report_completed' || event_type === 'report_failed') &&
    onOpenReportModal
  ) {
    onOpenReportModal()
    return
  }

  if (!navigation_url) return

  // Relatórios não têm mais tela própria. Notificações antigas (e qualquer
  // origem que ainda mande a URL da tela) precisam cair no modal.
  const isReportUrl =
    /^\/relatorios\b/.test(navigation_url) ||
    /^\/operacao\?.*\btab=relatorios\b/.test(navigation_url)
  if (isReportUrl && onOpenReportModal) {
    onCloseDrawer?.()
    onOpenReportModal()
    return
  }

  const billingMatch = navigation_url.match(/^\/cobrancas\/(.+)$/)
  if (billingMatch && onOpenBillingDetail) {
    onOpenBillingDetail(billingMatch[1])
    return
  }

  const nfseMatch = navigation_url.match(/^\/notas-fiscais\/(.+)$/)
  if (nfseMatch && onOpenNfseDetail) {
    onOpenNfseDetail(nfseMatch[1])
    return
  }

  const expenseMatch = navigation_url.match(/^\/contas-a-pagar\/(.+)$/)
  if (expenseMatch) {
    onCloseDrawer?.()
    router.push(`/operacao?tab=contas-a-pagar&expense_uuid=${expenseMatch[1]}`)
    return
  }

  const disputeMatch = navigation_url.match(
    /[?&]tab=contestacoes(?:&dispute=([^&]*))?/
  )
  if (disputeMatch && onOpenBalanceModal) {
    onCloseDrawer?.()
    onOpenBalanceModal('contestacoes', disputeMatch[1])
    return
  }

  onCloseDrawer?.()
  router.push(navigation_url)
}
